/**
 * CodeBlock Component for Project Inbox
 *
 * Renders code blocks with syntax highlighting using Monaco Editor.
 * Supports viewing and optional editing of code.
 */
import { useState } from 'react'
import Editor from '@monaco-editor/react'
import {
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'

interface CodeBlockProps {
  code: string
  language?: string
  filename?: string
  editable?: boolean
  onChange?: (code: string) => void
  maxHeight?: number
  minHeight?: number
}

// Map common language aliases to Monaco language IDs
const languageMap: Record<string, string> = {
  js: 'javascript',
  ts: 'typescript',
  jsx: 'javascript',
  tsx: 'typescript',
  py: 'python',
  rb: 'ruby',
  rs: 'rust',
  sh: 'shell',
  bash: 'shell',
  yml: 'yaml',
  md: 'markdown',
}

const getMonacoLanguage = (language?: string, filename?: string): string => {
  if (language) {
    const normalizedLang = language.toLowerCase()
    return languageMap[normalizedLang] || normalizedLang
  }

  // Infer from filename extension
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase()
    if (ext) {
      const mapping: Record<string, string> = {
        js: 'javascript',
        ts: 'typescript',
        jsx: 'javascript',
        tsx: 'typescript',
        py: 'python',
        rb: 'ruby',
        rs: 'rust',
        go: 'go',
        java: 'java',
        c: 'c',
        cpp: 'cpp',
        h: 'c',
        hpp: 'cpp',
        cs: 'csharp',
        html: 'html',
        css: 'css',
        scss: 'scss',
        json: 'json',
        yaml: 'yaml',
        yml: 'yaml',
        md: 'markdown',
        sql: 'sql',
        sh: 'shell',
        bash: 'shell',
        xml: 'xml',
      }
      return mapping[ext] || 'plaintext'
    }
  }

  return 'plaintext'
}

export default function CodeBlock({
  code,
  language,
  filename,
  editable = false,
  onChange,
  maxHeight = 400,
  minHeight = 100,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedCode, setEditedCode] = useState(code)

  const monacoLanguage = getMonacoLanguage(language, filename)

  // Calculate height based on line count
  const lineCount = code.split('\n').length
  const calculatedHeight = Math.min(Math.max(lineCount * 20 + 20, minHeight), maxHeight)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editable && isEditing ? editedCode : code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      setEditedCode(value)
      onChange?.(value)
    }
  }

  const handleSave = () => {
    onChange?.(editedCode)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditedCode(code)
    setIsEditing(false)
  }

  return (
    <div className="rounded-lg overflow-hidden border border-gray-700 bg-[#1e1e1e]">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="h-4 w-4 text-gray-400" />
          {filename && (
            <span className="text-sm text-gray-300 font-mono">{filename}</span>
          )}
          {!filename && language && (
            <span className="text-xs text-gray-400 uppercase">{language}</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Edit/Save buttons */}
          {editable && (
            <>
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="px-2 py-1 text-xs text-green-400 hover:bg-gray-700 rounded"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-2 py-1 text-xs text-gray-400 hover:bg-gray-700 rounded"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-2 py-1 text-xs text-gray-400 hover:bg-gray-700 rounded"
                >
                  Edit
                </button>
              )}
            </>
          )}

          {/* Copy button */}
          <button
            onClick={handleCopy}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded"
            title="Copy code"
          >
            {copied ? (
              <CheckIcon className="h-4 w-4 text-green-400" />
            ) : (
              <ClipboardDocumentIcon className="h-4 w-4" />
            )}
          </button>

          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronUpIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Code editor/viewer */}
      {!collapsed && (
        <div style={{ height: calculatedHeight }}>
          <Editor
            height="100%"
            language={monacoLanguage}
            value={editable && isEditing ? editedCode : code}
            onChange={handleEditorChange}
            theme="vs-dark"
            options={{
              readOnly: !editable || !isEditing,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 13,
              fontFamily: "'Fira Code', 'Monaco', 'Consolas', monospace",
              lineNumbers: 'on',
              folding: true,
              wordWrap: 'on',
              automaticLayout: true,
              scrollbar: {
                vertical: 'auto',
                horizontal: 'auto',
                verticalScrollbarSize: 8,
                horizontalScrollbarSize: 8,
              },
              padding: { top: 8, bottom: 8 },
              renderLineHighlight: isEditing ? 'line' : 'none',
              cursorStyle: 'line',
              cursorBlinking: 'smooth',
            }}
            loading={
              <div className="flex items-center justify-center h-full text-gray-400">
                Loading editor...
              </div>
            }
          />
        </div>
      )}

      {/* Collapsed indicator */}
      {collapsed && (
        <div className="px-3 py-2 text-sm text-gray-500 italic">
          {lineCount} lines (click to expand)
        </div>
      )}
    </div>
  )
}
